import React from 'react';
import { Spinner } from './Spinner';

/**
 * Button
 *
 * Variants:  primary | secondary | danger | ghost
 * Sizes:     sm | md | lg
 * fullWidth: boolean
 *
 * Usage:
 *   <Button variant="primary" size="md" onClick={fn}>Save</Button>
 *   <Button variant="secondary" loading>Saving...</Button>
 *   <Button variant="danger" size="sm">Delete</Button>
 */

const base = [
  'inline-flex items-center justify-center gap-2',
  'font-semibold rounded-[var(--radius-lg)]',
  'transition-all duration-[var(--transition-base)]',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
  'select-none',
].join(' ');

const variants = {
  primary: [
    'bg-[var(--color-orange)] text-white',
    'hover:bg-[var(--color-orange-hover)]',
    'active:scale-[0.98]',
    'focus-visible:ring-[var(--color-orange)]',
  ].join(' '),

  secondary: [
    'bg-white text-[var(--color-text-primary)]',
    'border border-[var(--color-border)]',
    'hover:bg-[var(--color-surface)] hover:border-[var(--color-border-strong)]',
    'active:scale-[0.98]',
    'focus-visible:ring-[var(--color-border-strong)]',
  ].join(' '),

  danger: [
    'bg-[var(--color-error-bg)] text-[var(--color-error)]',
    'border border-[var(--color-error-border)]',
    'hover:bg-red-100',
    'active:scale-[0.98]',
    'focus-visible:ring-[var(--color-error)]',
  ].join(' '),

  ghost: [
    'bg-transparent text-[var(--color-text-secondary)]',
    'hover:bg-[var(--color-surface)] hover:text-[var(--color-text-primary)]',
    'active:scale-[0.98]',
    'focus-visible:ring-[var(--color-border-strong)]',
  ].join(' '),
};

const sizes = {
  sm: 'h-8 px-3 text-xs min-w-[var(--touch-target)]',
  md: 'h-10 px-4 text-sm min-w-[var(--touch-target)]',
  lg: 'h-12 px-6 text-base min-w-[var(--touch-target)]',
};

const mobileSizes = {
  sm: 'sm:h-8 sm:px-3 sm:text-xs h-11 px-4 text-sm',
  md: 'sm:h-10 sm:px-4 sm:text-sm h-12 px-5 text-base',
  lg: 'sm:h-12 sm:px-6 sm:text-base h-14 px-6 text-lg',
};

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  mobileExpand = false,
  icon = null,
  iconRight = null,
  className = '',
  children,
  ...props
}) {
  const sizeClass = mobileExpand ? mobileSizes[size] : sizes[size];

  return (
    <button
      disabled={disabled || loading}
      className={[
        base,
        variants[variant],
        sizeClass,
        fullWidth ? 'w-full' : '',
        className,
      ].filter(Boolean).join(' ')}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : icon}
      {children}
      {!loading && iconRight}
    </button>
  );
}

/**
 * IconButton — square button for icon-only actions
 */
export function IconButton({
  variant = 'ghost',
  size = 'md',
  className = '',
  children,
  ...props
}) {
  const iconSizes = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  return (
    <button
      className={[
        base,
        variants[variant],
        iconSizes[size],
        'rounded-[var(--radius-md)] p-0 shrink-0',
        className,
      ].filter(Boolean).join(' ')}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Toggle — Apple-style switch
 */
export function Toggle({ checked, onChange, label, disabled = false, className = '' }) {
  return (
    <label className={[
      'inline-flex items-center gap-2.5 cursor-pointer select-none',
      disabled ? 'opacity-50 cursor-not-allowed' : '',
      className,
    ].filter(Boolean).join(' ')}>
      <button
        role="switch"
        type="button"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={[
          'relative inline-flex shrink-0 h-[22px] w-[38px]',
          'rounded-full border-2 border-transparent',
          'transition-colors duration-200 ease-in-out',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-orange)]',
          checked ? 'bg-[var(--color-navy)]' : 'bg-[var(--color-border-strong)]',
        ].join(' ')}
      >
        <span className={[
          'pointer-events-none inline-block h-[18px] w-[18px]',
          'rounded-full bg-white shadow-sm',
          'transition-transform duration-200 ease-in-out',
          checked ? 'translate-x-4' : 'translate-x-0',
        ].join(' ')} />
      </button>
      {label && (
        <span className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</span>
      )}
    </label>
  );
}

export default Button;
